import React, { useState, useCallback, useEffect } from 'react';

import { SyncOutlined, CheckCircleOutlined, ClockCircleOutlined, ThunderboltOutlined, AppstoreOutlined, QuestionCircleFilled } from '@ant-design/icons';
import JumpModeAnimation from '@components/operator/distribute/JumpModeAnimation';
import GlobalBarcodeInput from '@components/operator/GlobalBarcodeInput';
import DistributeConceptModal from '@shared/distribute/DistributeConceptModal';
import { useBarcodeInput, useBarcodeInputContext } from '@contexts/operator/BarcodeInputContext';
import { DistributeProvider, useDistribute } from '@contexts/operator/DistributeContext';
import PageHeader from '@shared/PageHeader';
import soundEffects from '@utils/soundEffects';
import { Card, InputNumber, Button, Space, Tooltip, Spin, Alert, Modal, Input, message, Table, Tag, Progress, Switch } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '@/utils/dateTime';
import './Distribute.css';

/**
 * 配貨作業頁面內容
 */
function DistributeContent() {
  const { t } = useTranslation();
  const { clear: clearBarcodeInput } = useBarcodeInputContext();

  // 從 Context 獲取狀態和方法
  const {
    pickingWave,
    distributeState,
    virtualOrders,
    gridRows,
    gridCols,
    loading,
    error,
    highlightedslotNumber,
    jumpMode,
    exceptionMode,
    resolveAndLoadWave,
    scanBarcode,
    clearDistributeEvents,
    setGridRows,
    setGridCols,
    setJumpMode,
    toggleExceptionMode,
    setVirtualOrders,
    getOrderByslotNumber,
    getVirtualOrderByslotNumber,
    getTotalDistributedForOrder,
    isOrderComplete
  } = useDistribute();

  // Modal 狀態
  const [clearModalVisible, setClearModalVisible] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  const [clearLoading, setClearLoading] = useState(false);

  // 訂單明細 Modal 狀態
  const [orderDetailModalVisible, setOrderDetailModalVisible] = useState(false);
  const [selectedslotNumber, setSelectedslotNumber] = useState(null);

  // 虛擬訂單明細 Modal 狀態
  const [virtualOrderModalVisible, setVirtualOrderModalVisible] = useState(false);
  const [selectedVirtualOrder, setSelectedVirtualOrder] = useState(null);

  // 跳號模式動畫狀態
  const [jumpAnimationActive, setJumpAnimationActive] = useState(false);
  const [jumpAnimationSource, setJumpAnimationSource] = useState(null);
  const [jumpAnimationIndex, setJumpAnimationIndex] = useState(null);
  const [jumpAnimationComplete, setJumpAnimationComplete] = useState(false);

  // 跳號模式：監聽高亮變化，觸發動畫
  useEffect(() => {
    if (jumpMode && highlightedslotNumber) {
      const cell = document.querySelector(`[data-picking-index="${highlightedslotNumber}"]`);
      if (cell) {
        // 檢查是否完成（正常訂單或虛擬訂單）
        const cellData = getOrderByslotNumber(highlightedslotNumber);
        const virtualOrder = getVirtualOrderByslotNumber(highlightedslotNumber);
        const isComplete = cellData && isOrderComplete(highlightedslotNumber);

        // 設置動畫狀態
        setJumpAnimationSource(cell);
        setJumpAnimationIndex(highlightedslotNumber);
        setJumpAnimationComplete(isComplete);
        setJumpAnimationActive(true);
      }
    }
  }, [jumpMode, highlightedslotNumber, getOrderByslotNumber, getVirtualOrderByslotNumber, isOrderComplete]);

  // 動畫結束回調
  const handleJumpAnimationEnd = useCallback(() => {
    setJumpAnimationActive(false);
    setJumpAnimationSource(null);
    setJumpAnimationIndex(null);
    setJumpAnimationComplete(false);
  }, []);

  // 處理條碼輸入
  const handleBarcodeSubmit = useCallback(async (code) => {
    console.log('[Distribute] Barcode scanned:', code);

    if (!pickingWave) {
      // 尚未載入 Wave，解析條碼（支援 PW / FL）
      console.log('[Distribute] Resolving barcode:', code);

      const result = await resolveAndLoadWave(code);

      if (result && !result.success) {
        soundEffects.playBuzzerError();
      }
    } else {
      // 已載入 Wave，掃描商品條碼
      console.log('[Distribute] Scanning product barcode:', code);

      const result = await scanBarcode(code, exceptionMode);

      if (result && result.success) {
        if (result.virtual) {
          message.info(result.message || t('operator.operations.distribute.exception_zone.item_added'));
        }
      } else {
        soundEffects.playBuzzerError();
      }
    }

    // 清空輸入
    clearBarcodeInput();
  }, [pickingWave, resolveAndLoadWave, scanBarcode, exceptionMode, t, clearBarcodeInput]);

  // 註冊條碼處理器到全域 BarcodeInput
  useBarcodeInput(handleBarcodeSubmit);

  // 打開清除確認 Modal
  const handleResetGrid = useCallback(() => {
    if (!pickingWave) {
      message.warning(t('operator.operations.distribute.reset.no_picking_list'));
      return;
    }

    setClearModalVisible(true);
    setClearConfirmText('');
  }, [pickingWave, t]);

  // 確認清除配貨記錄
  const handleConfirmClear = useCallback(async () => {
    if (clearConfirmText !== 'clear') {
      message.error(t('operator.operations.distribute.reset.incorrect_confirm'));
      return;
    }

    setClearLoading(true);

    try {
      const result = await clearDistributeEvents();

      if (result.success) {
        message.success(t('operator.operations.distribute.reset.success'));
        setClearModalVisible(false);
        setClearConfirmText('');
      } else {
        message.error(result.message || t('operator.operations.distribute.reset.error'));
      }
    } catch (err) {
      message.error(err.message || t('operator.operations.distribute.reset.error'));
    } finally {
      setClearLoading(false);
    }
  }, [clearConfirmText, clearDistributeEvents, t]);

  // 取消清除
  const handleCancelClear = useCallback(() => {
    setClearModalVisible(false);
    setClearConfirmText('');
  }, []);

  // 打開訂單明細 Modal
  const handleOpenOrderDetail = useCallback((slotNumber) => {
    setSelectedslotNumber(slotNumber);
    setOrderDetailModalVisible(true);
  }, []);

  // 關閉訂單明細 Modal
  const handleCloseOrderDetail = useCallback(() => {
    setOrderDetailModalVisible(false);
    setSelectedslotNumber(null);
  }, []);

  // 打開虛擬訂單明細 Modal
  const handleOpenVirtualOrderDetail = useCallback((virtualOrder) => {
    setSelectedVirtualOrder(virtualOrder);
    setVirtualOrderModalVisible(true);
  }, []);

  // 關閉虛擬訂單明細 Modal
  const handleCloseVirtualOrderDetail = useCallback(() => {
    setVirtualOrderModalVisible(false);
    setSelectedVirtualOrder(null);
  }, []);

  // 刪除虛擬訂單
  const handleDeleteVirtualOrder = useCallback((slotNumber) => {
    const newVirtualOrders = virtualOrders.filter(v => v.slotNumber !== slotNumber);
    // 使用 Context 的 setVirtualOrders
    setVirtualOrders(newVirtualOrders);
    setVirtualOrderModalVisible(false);
    setSelectedVirtualOrder(null);
    message.success(t('operator.operations.distribute.exception_zone.virtual_deleted', {
      defaultValue: '已刪除異常品'
    }));
  }, [virtualOrders, setVirtualOrders, t]);

  // 取得選中的訂單資料
  const selectedOrderData = selectedslotNumber
    ? getOrderByslotNumber(selectedslotNumber)
    : null;

  // 渲染 Grid 格子
  const renderGridCells = () => {
    const cells = [];
    const totalCells = gridRows * gridCols;

    for (let i = 0; i < totalCells; i++) {
      const slotNumber = i + 1;

      // 檢查是否有異常品（異常區開啟時）
      const virtualOrder = exceptionMode ? getVirtualOrderByslotNumber(slotNumber) : null;

      // 從 distributeState 取得該格子的資料
      const cellData = getOrderByslotNumber(slotNumber);

      // 判斷格子類型
      const isVirtualOrder = !!virtualOrder;
      const hasOrder = !!cellData;
      const hasAnyContent = hasOrder || isVirtualOrder;

      // 檢查是否高亮（剛掃描的格子）
      const isHighlighted = highlightedslotNumber === slotNumber;

      // 渲染虛擬訂單格子
      if (isVirtualOrder) {
        cells.push(
          <div
            key={i}
            className="distribute-grid__cell distribute-grid__cell--virtual"
            data-has-order={true}
            data-virtual={true}
            data-highlighted={isHighlighted && !jumpMode}
            data-picking-index={slotNumber}
            onClick={() => handleOpenVirtualOrderDetail(virtualOrder)}
          >
            <div className="distribute-grid__order">
              {/* 虛擬訂單標記 */}
              <div className="distribute-grid__virtual-badge">
                {t('operator.operations.distribute.exception_zone.virtual_badge', { defaultValue: '異常' })}
              </div>
              {/* slotNumber */}
              <div className="distribute-grid__picking-index distribute-grid__picking-index--virtual">
                {slotNumber}
              </div>
              {/* 商品資訊 */}
              <div className="distribute-grid__virtual-info">
                {virtualOrder.product ? (
                  <>
                    {/* 有商品資料：顯示商品名稱 */}
                    <div className="distribute-grid__virtual-name">{virtualOrder.product.name}</div>
                    <div className="distribute-grid__virtual-code">{virtualOrder.product.sku || virtualOrder.code}</div>
                  </>
                ) : (
                  <>
                    {/* 無商品資料：顯示未知商品 */}
                    <div className="distribute-grid__virtual-unknown">
                      {t('operator.operations.distribute.exception_zone.unknown_product', { defaultValue: '未知商品' })}
                    </div>
                    <div className="distribute-grid__virtual-code">{virtualOrder.code}</div>
                  </>
                )}
                <div className="distribute-grid__virtual-quantity">
                  x {virtualOrder.quantity}
                </div>
              </div>
            </div>
          </div>
        );
      }
      // 渲染正常訂單格子
      else if (hasOrder) {
        // 計算已配貨和總數量
        const totalDistributed = getTotalDistributedForOrder(slotNumber);
        const totalRequired = cellData.distributed_items.reduce((sum, item) => sum + item.required_quantity, 0);

        // 檢查是否完成
        const isComplete = isOrderComplete(slotNumber);

        cells.push(
          <div
            key={i}
            className="distribute-grid__cell"
            data-has-order={true}
            data-disabled={false}
            data-completed={isComplete}
            data-highlighted={isHighlighted && !jumpMode}
            data-picking-index={slotNumber}
            onClick={() => handleOpenOrderDetail(slotNumber)}
          >
            <div className="distribute-grid__order">
              {/* 主要顯示：slotNumber 大號數字 */}
              <div className="distribute-grid__picking-index">{slotNumber}</div>

              {/* 配貨進度 */}
              <div className="distribute-grid__order-items">
                <span className="distribute-grid__scanned-count">{totalDistributed}</span>
                /
                <span className="distribute-grid__total-count">{totalRequired}</span>
              </div>
            </div>
          </div>
        );
      }
      // 渲染空格子
      else {
        cells.push(
          <div
            key={i}
            className="distribute-grid__cell"
            data-has-order={false}
            data-disabled={true}
            data-picking-index={slotNumber}
          >
            <div className="distribute-grid__empty distribute-grid__empty--disabled">
              <span className="distribute-grid__index">{slotNumber}</span>
            </div>
          </div>
        );
      }
    }

    return cells;
  };

  return (
    <div className="distribute-page">
      {/* 左側：分配訂單區（Grid） */}
      <div className="distribute-page__left">
        <Card
          title={
            <Space>
              <span>
                {t('operator.operations.distribute.grid.title', { defaultValue: '配貨區' })}
              </span>
              {pickingWave && (
                <span style={{ fontWeight: 'normal', fontSize: '14px', color: '#999' }}>
                  {pickingWave.name}
                </span>
              )}
            </Space>
          }
          extra={
            <Space>
              <Tooltip title={t('operator.operations.distribute.jump_mode.tooltip', { defaultValue: '開啟後掃描時會顯示全螢幕跳號動畫' })}>
                <Space>
                  <ThunderboltOutlined style={{ color: jumpMode ? '#faad14' : '#999' }} />
                  <span style={{ fontSize: '12px', color: '#999' }}>
                    {t('operator.operations.distribute.jump_mode.label', { defaultValue: '跳號模式' })}
                  </span>
                  <Switch
                    checked={jumpMode}
                    onChange={setJumpMode}
                    size="small"
                  />
                </Space>
              </Tooltip>
              <Tooltip title={
                !pickingWave
                  ? t('operator.operations.distribute.exception_zone.disabled_tooltip')
                  : t('operator.operations.distribute.exception_zone.tooltip')
              }>
                <Space>
                  <AppstoreOutlined style={{ color: exceptionMode ? '#1890ff' : (!pickingWave ? '#ccc' : '#999') }} />
                  <span style={{ fontSize: '12px', color: !pickingWave ? '#ccc' : '#999' }}>
                    {t('operator.operations.distribute.exception_zone.label', { defaultValue: '異常區' })}
                  </span>
                  <Switch
                    checked={exceptionMode}
                    onChange={toggleExceptionMode}
                    disabled={!pickingWave}
                    size="small"
                  />
                </Space>
              </Tooltip>
              <span>{t('operator.operations.distribute.grid.rows_label', { defaultValue: '行數' })}:</span>
              <InputNumber
                min={2}
                max={10}
                value={gridRows}
                onChange={setGridRows}
                style={{ width: '60px' }}
              />
              <span>{t('operator.operations.distribute.grid.cols_label', { defaultValue: '列數' })}:</span>
              <InputNumber
                min={2}
                max={10}
                value={gridCols}
                onChange={setGridCols}
                style={{ width: '60px' }}
              />
              <span style={{ color: '#999' }}>
                ({gridRows} x {gridCols} = {gridRows * gridCols})
              </span>
              <Tooltip title={t('operator.operations.distribute.grid.reset_tooltip', { defaultValue: '重新排列訂單' })}>
                <Button
                  icon={<SyncOutlined />}
                  onClick={handleResetGrid}
                  disabled={!pickingWave}
                >
                  {t('operator.operations.distribute.grid.reset_button', { defaultValue: '重排' })}
                </Button>
              </Tooltip>
            </Space>
          }
          className="distribute-page__grid-card"
          styles={{ body: { padding: '16px' } }}
        >
          <div
            className="distribute-grid"
            style={{
              gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
              gridTemplateRows: `repeat(${gridRows}, 1fr)`,
            }}
          >
            {renderGridCells()}
          </div>
        </Card>
      </div>

      {/* 右側：條碼輸入 */}
      <div className="distribute-page__right">
        <Card
          title={t('operator.operations.distribute.scanner.title', { defaultValue: '條碼掃描' })}
          className="distribute-page__scanner-card"
          styles={{ body: { padding: '16px' } }}
        >
          <div className="distribute-page__scanner-content">
            <GlobalBarcodeInput
              placeholder={t('operator.operations.distribute.scanner.placeholder', {
                defaultValue: '請掃描波次 (PW) 或出貨單 (FL) 條碼',
              })}
            />

            {/* 掃描提示 */}
            <div className="distribute-page__scanner-hint">
              {!pickingWave ? (
                <div className="distribute-page__hint-primary">
                  {t('operator.operations.distribute.scanner.hint_scan_wave', {
                    defaultValue: '請掃描波次 (PW-xxx) 或出貨單 (FL-xxx) 條碼',
                  })}
                </div>
              ) : (
                <div className="distribute-page__hint-secondary">
                  {t('operator.operations.distribute.scanner.hint_scan_product', {
                    defaultValue: '請掃描商品條碼進行配貨',
                  })}
                </div>
              )}
            </div>

            {/* Loading 狀態 */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
              </div>
            )}

            {/* Error 狀態 */}
            {error && (
              <Alert
                type="error"
                message={error}
                closable
                onClose={() => {}}
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Card>

        {/* 統計資訊 */}
        {pickingWave && (
          <Card
            title={t('operator.operations.distribute.stats.title', { defaultValue: '配貨統計' })}
            size="small"
            style={{ marginTop: '16px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <span style={{ color: '#999' }}>
                  {t('operator.operations.distribute.stats.total_orders', { defaultValue: '總訂單數' })}:
                </span>{' '}
                <span style={{ fontWeight: 600 }}>{distributeState.length}</span>
              </div>
              <div>
                <span style={{ color: '#999' }}>
                  {t('operator.operations.distribute.stats.completed_orders', { defaultValue: '已完成' })}:
                </span>{' '}
                <span style={{ fontWeight: 600, color: '#52c41a' }}>
                  {distributeState.filter((_, idx) => isOrderComplete(idx + 1)).length}
                </span>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* 清除確認 Modal */}
      <Modal
        title={t('operator.operations.distribute.reset.modal_title')}
        open={clearModalVisible}
        onOk={handleConfirmClear}
        onCancel={handleCancelClear}
        confirmLoading={clearLoading}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        okButtonProps={{ danger: true, disabled: clearConfirmText !== 'clear' }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Alert
            type="warning"
            message={t('operator.operations.distribute.reset.warning')}
            showIcon
          />
        </div>
        <p>
          {t('operator.operations.distribute.reset.instruction')}
        </p>
        <Input
          placeholder="clear"
          value={clearConfirmText}
          onChange={(e) => setClearConfirmText(e.target.value)}
          onPressEnter={handleConfirmClear}
          autoFocus
        />
      </Modal>

      {/* 訂單明細 Modal */}
      <Modal
        title={
          <Space>
            <span>
              {t('operator.operations.distribute.order_detail.title')}
            </span>
            {selectedOrderData && (
              <>
                <Tag color="blue">#{selectedslotNumber}</Tag>
                {isOrderComplete(selectedslotNumber) && (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {t('operator.operations.distribute.order_detail.completed')}
                  </Tag>
                )}
              </>
            )}
          </Space>
        }
        open={orderDetailModalVisible}
        onCancel={handleCloseOrderDetail}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseOrderDetail}>
            {t('common.button.close')}
          </Button>
        ]}
        width={800}
        className="distribute-order-detail-modal"
      >
        {selectedOrderData && (
          <div className="distribute-order-detail">
            {/* 訂單基本資訊 */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.order_detail.order_number')}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '16px', color: '#1890ff' }}>
                    {selectedOrderData.order.name}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.order_detail.customer')}
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedOrderData.order.receiver_name || '-'}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.order_detail.slot_number')}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '18px', color: '#1890ff' }}>
                    #{selectedslotNumber}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.order_detail.progress')}
                  </div>
                  <Progress
                    percent={Math.round((getTotalDistributedForOrder(selectedslotNumber) /
                      selectedOrderData.distributed_items.reduce((sum, item) => sum + item.required_quantity, 0)) * 100)}
                    status={isOrderComplete(selectedslotNumber) ? 'success' : 'active'}
                    size="small"
                  />
                </div>
              </div>
            </Card>

            {/* 商品明細表格 */}
            <Table
              dataSource={selectedOrderData.distributed_items}
              rowKey="order_item_id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('operator.operations.distribute.order_detail.table.product'),
                  dataIndex: 'product_name',
                  key: 'product_name',
                  width: '30%',
                  render: (text, record) => (
                    <div>
                      <div style={{ fontWeight: 500 }}>{text}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        SKU: {record.sku}
                      </div>
                      {record.barcode && (
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {t('operator.operations.distribute.order_detail.table.barcode')}: {record.barcode}
                        </div>
                      )}
                    </div>
                  )
                },
                {
                  title: t('operator.operations.distribute.order_detail.table.batch'),
                  dataIndex: 'batch',
                  key: 'batch',
                  width: '15%',
                  render: (text) => text || '-'
                },
                {
                  title: t('operator.operations.distribute.order_detail.table.required'),
                  dataIndex: 'required_quantity',
                  key: 'required_quantity',
                  width: '10%',
                  align: 'center',
                  render: (text) => (
                    <span style={{ fontWeight: 500, fontSize: '16px' }}>{text}</span>
                  )
                },
                {
                  title: t('operator.operations.distribute.order_detail.table.distributed'),
                  dataIndex: 'distributed_quantity',
                  key: 'distributed_quantity',
                  width: '10%',
                  align: 'center',
                  render: (text, record) => {
                    const isComplete = text >= record.required_quantity;
                    return (
                      <span style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: isComplete ? '#52c41a' : '#1890ff'
                      }}>
                        {text}
                      </span>
                    );
                  }
                },
                {
                  title: t('operator.operations.distribute.order_detail.table.status'),
                  key: 'status',
                  width: '15%',
                  align: 'center',
                  render: (_, record) => {
                    const isComplete = record.distributed_quantity >= record.required_quantity;
                    return isComplete ? (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        {t('operator.operations.distribute.order_detail.table.complete')}
                      </Tag>
                    ) : (
                      <Tag color="processing" icon={<ClockCircleOutlined />}>
                        {t('operator.operations.distribute.order_detail.table.pending')}
                      </Tag>
                    );
                  }
                },
                {
                  title: t('operator.operations.distribute.order_detail.table.last_scan'),
                  key: 'last_scan',
                  width: '20%',
                  render: (_, record) => {
                    if (!record.last_distributed_at) return '-';
                    return (
                      <div style={{ fontSize: '12px' }}>
                        <div style={{ color: '#999' }}>
                          {formatDateTime(record.last_distributed_at)}
                        </div>
                        {record.last_distributed_by && (
                          <div style={{ color: '#1890ff' }}>
                            {record.last_distributed_by.name}
                          </div>
                        )}
                      </div>
                    );
                  }
                }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 虛擬訂單明細 Modal */}
      <Modal
        title={
          <Space>
            <span>
              {t('operator.operations.distribute.exception_zone.modal_title', { defaultValue: '異常品明細' })}
            </span>
            {selectedVirtualOrder && (
              <Tag color="orange">#{selectedVirtualOrder.slotNumber}</Tag>
            )}
          </Space>
        }
        open={virtualOrderModalVisible}
        onCancel={handleCloseVirtualOrderDetail}
        footer={[
          <Button
            key="delete"
            danger
            onClick={() => handleDeleteVirtualOrder(selectedVirtualOrder?.slotNumber)}
          >
            {t('common.button.delete', { defaultValue: '刪除' })}
          </Button>,
          <Button key="close" type="primary" onClick={handleCloseVirtualOrderDetail}>
            {t('common.button.close', { defaultValue: '關閉' })}
          </Button>
        ]}
        width={600}
      >
        {selectedVirtualOrder && (
          <div className="virtual-order-detail">
            {/* 基本資訊 */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.exception_zone.modal_slot_number', { defaultValue: '配貨位置' })}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '18px', color: '#ff9800' }}>
                    #{selectedVirtualOrder.slotNumber}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.exception_zone.modal_quantity', { defaultValue: '掃描數量' })}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '18px', color: '#ff6f00' }}>
                    x {selectedVirtualOrder.quantity}
                  </div>
                </div>
              </div>
            </Card>

            {/* 商品資訊 */}
            <Card
              size="small"
              title={t('operator.operations.distribute.exception_zone.modal_product_info', { defaultValue: '商品資訊' })}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedVirtualOrder.product ? (
                  <>
                    {/* 有商品資料 */}
                    <div>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                        {t('operator.operations.distribute.exception_zone.modal_product_name', { defaultValue: '商品名稱' })}
                      </div>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: '#1976d2',
                        background: '#e3f2fd',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #90caf9',
                        wordBreak: 'break-all'
                      }}>
                        {selectedVirtualOrder.product.name}
                      </div>
                    </div>

                    <div>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                        {t('operator.operations.distribute.exception_zone.modal_sku', { defaultValue: 'SKU' })}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 500 }}>
                        {selectedVirtualOrder.product.sku}
                      </div>
                    </div>

                    {selectedVirtualOrder.product.barcode && (
                      <div>
                        <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                          {t('operator.operations.distribute.exception_zone.modal_barcode', { defaultValue: '條碼' })}
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                          {selectedVirtualOrder.product.barcode}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* 無商品資料 */}
                    <div>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                        {t('operator.operations.distribute.exception_zone.modal_status', { defaultValue: '狀態' })}
                      </div>
                      <Tag color="red">
                        {t('operator.operations.distribute.exception_zone.unknown_product', { defaultValue: '未知商品' })}
                      </Tag>
                    </div>

                    <div>
                      <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                        {t('operator.operations.distribute.exception_zone.modal_scanned_code', { defaultValue: '掃描代碼' })}
                      </div>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: '#d84315',
                        background: '#fff3e0',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #ffccbc',
                        wordBreak: 'break-all'
                      }}>
                        {selectedVirtualOrder.code}
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.exception_zone.modal_created_at', { defaultValue: '首次掃描時間' })}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {formatDateTime(selectedVirtualOrder.created_at)}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>
                    {t('operator.operations.distribute.exception_zone.modal_last_scanned_at', { defaultValue: '最後掃描時間' })}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {formatDateTime(selectedVirtualOrder.last_scanned_at)}
                  </div>
                </div>
              </div>
            </Card>

            {/* 提示資訊 */}
            <Alert
              type={selectedVirtualOrder.product ? 'info' : 'warning'}
              message={selectedVirtualOrder.product
                ? t('operator.operations.distribute.exception_zone.modal_info_found', {
                    defaultValue: '此商品已在系統中找到，但不在當前 Picking List 中'
                  })
                : t('operator.operations.distribute.exception_zone.modal_warning', {
                    defaultValue: '此商品不在系統中，請確認是否為掃描錯誤'
                  })
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        )}
      </Modal>

      {/* 跳號模式動畫 */}
      {jumpAnimationActive && (
        <JumpModeAnimation
          slotNumber={jumpAnimationIndex}
          isComplete={jumpAnimationComplete}
          sourceElement={jumpAnimationSource}
          onAnimationEnd={handleJumpAnimationEnd}
        />
      )}
    </div>
  );
}

/**
 * 配貨作業頁面（帶 Provider）
 */
export default function Distribute() {
  const { t } = useTranslation();
  const [conceptVisible, setConceptVisible] = useState(false);

  return (
    <div>
      <PageHeader
        items={['operations', 'operations.distribute']}
        guideButton={
          <Tooltip title={t('concepts.distribute.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
      <DistributeProvider>
        <DistributeContent />
      </DistributeProvider>
      <DistributeConceptModal
        visible={conceptVisible}
        onClose={() => setConceptVisible(false)}
      />
    </div>
  );
}
